import { Button, Checkbox, Stack, TextField } from '@mui/material'
import React from 'react'
import { useFormContext, Controller } from 'react-hook-form'

import passwordOutcome from 'components/authentication/passwordOutcome'
import { PasswordRules } from 'components/authentication/PasswordRules'
import TrackedExternalLink from 'components/common/TrackedExternalLink'
import theme from 'theme'

const isFormInvalid = ({
  passwordOutcome,
  companyName,
  teamName,
  allowUsStorage
}) => {
  const passwordInvalid = passwordOutcome.some((rule) => rule.invalid)

  return (
    passwordInvalid ||
    !allowUsStorage ||
    !companyName?.length ||
    !teamName?.length
  )
}

export const AccountSetupForm: React.FC<{
  disabled: boolean
  email: string | string[]
  firstName?: string | string[]
  lastName?: string | string[]
}> = ({ disabled, email, firstName, lastName }) => {
  const name = [firstName, lastName].join(' ')

  const { watch, control } = useFormContext()

  return (
    <Stack data-test-account-setup-form spacing={2}>
      <div>
        {name && <div data-test-account-setup-name={name}>{name}</div>}
        <div data-test-account-setup-email={email}>{email}</div>
      </div>
      <Controller
        control={control}
        name="password"
        render={({ field, fieldState: { error } }) => (
          <TextField
            autoFocus
            data-test-account-setup-password
            disabled={disabled}
            error={Boolean(error?.message)}
            helperText={error?.message}
            label="Create password"
            type="password"
            {...field}
          />
        )}
        rules={{ required: 'Please enter a password' }}
      />
      <Controller
        control={control}
        name="confirmPassword"
        render={({ field, fieldState: { error } }) => (
          <TextField
            data-test-account-setup-confirm-password
            disabled={disabled}
            error={Boolean(error?.message)}
            helperText={error?.message}
            label="Confirm password"
            type="password"
            {...field}
          />
        )}
        rules={{ required: 'Please enter a password' }}
      />
      <div css={{ fontSize: theme.fontSizes[0] }}>
        <PasswordRules
          fieldsStyle={{
            wrapper: {
              marginBottom: theme.space[0]
            }
          }}
          passwordOutcome={passwordOutcome(
            watch('password'),
            watch('confirmPassword')
          )}
        />
      </div>
      <Controller
        control={control}
        name="companyName"
        render={({ field, fieldState: { error } }) => (
          <TextField
            data-test-account-setup-company-name
            disabled={disabled}
            error={Boolean(error?.message)}
            helperText={error?.message}
            label="Company Name"
            {...field}
          />
        )}
        rules={{ required: 'Please enter a company name', maxLength: 50 }}
      />
      <Controller
        control={control}
        name="teamName"
        render={({ field, fieldState: { error } }) => (
          <TextField
            data-test-account-setup-team-name
            disabled={disabled}
            error={Boolean(error?.message)}
            helperText={error?.message}
            label="Team Name"
            {...field}
          />
        )}
        rules={{ required: 'Please enter a team name', maxLength: 50 }}
      />
      <label
        data-test-allow-us-storage
        css={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: theme.space[4],
          cursor: 'pointer'
        }}
        htmlFor="allowUsStorage"
      >
        <Controller
          control={control}
          name="allowUsStorage"
          render={({ field }) => (
            <Checkbox
              data-test-allow-us-storage-checkbox
              checked={field.value}
              name="allowUsStorage"
              {...field}
            />
          )}
          rules={{ required: 'This field is required' }}
        />

        <span
          css={{
            marginLeft: theme.space[3],
            fontSize: theme.fontSizes[1]
          }}
        >
          I have read and agree to the{' '}
          <TrackedExternalLink href="https://www.gremlin.com/privacy/">
            privacy policy
          </TrackedExternalLink>
        </span>
      </label>
      <Button
        data-test-account-setup-continue
        disabled={
          isFormInvalid({
            passwordOutcome: passwordOutcome(
              watch('password'),
              watch('confirmPassword')
            ),
            companyName: watch('companyName'),
            teamName: watch('teamName'),
            allowUsStorage: watch('allowUsStorage')
          }) || disabled
        }
        type="submit"
        variant="contained"
      >
        Start Trial
      </Button>
    </Stack>
  )
}
